import { Button } from "./ui/button";
import { ShieldCheck, DollarSign, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_foreclosure-leads/artifacts/byzhzggm_4c101ca4-c76d-42c6-a48a-547875053356.jpg";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] } },
};

export default function Hero() {
  const scroll = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      className="relative min-h-[100svh] w-full overflow-hidden -mt-[96px] pt-[112px] sm:pt-[120px]"
      data-testid="hero-section"
    >
      {/* Full-page background image with subtle Ken Burns */}
      <motion.img
        src={HERO_IMG}
        alt="Luxury family home at sunset"
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 8, ease: "easeOut" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/70 via-[#0F172A]/45 to-[#0F172A]/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/60 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 min-h-[calc(100svh-112px)] sm:min-h-[calc(100svh-120px)] flex flex-col justify-center py-12 sm:py-16">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-3xl"
        >
          <motion.div variants={item} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F7F5F0]/15 backdrop-blur-sm border border-[#F7F5F0]/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#FF008F] animate-pulse" />
            <span className="text-[11px] sm:text-xs text-[#F7F5F0] tracking-wide">Surplus Funds Recovery Specialists</span>
          </motion.div>

          <motion.h1
            variants={item}
            className="font-serif-display text-[2.5rem] leading-[1.04] sm:text-5xl lg:text-7xl text-[#F7F5F0] drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)]"
          >
            The money from your <span className="italic text-[#FF008F]">foreclosed home</span> may still belong to you.
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-5 sm:mt-6 text-base sm:text-lg lg:text-xl text-[#F7F5F0]/90 max-w-2xl leading-relaxed"
          >
            When a property sells at foreclosure for more than what was owed, the leftover funds — sometimes tens of thousands of dollars — legally belong to the previous owner. We help families recover what's rightfully theirs.
          </motion.p>

          <motion.div variants={item} className="mt-7 sm:mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <Button
              onClick={() => scroll("#claim")}
              className="bg-[#FF008F] hover:bg-[#D60079] text-white rounded-full px-7 sm:px-8 h-12 text-lg font-bold transition-all duration-300 hover:-translate-y-0.5 shadow-xl shadow-[#FF008F]/30"
              data-testid="hero-cta-primary"
            >
              Check My Free Claim <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => scroll("#about")}
              variant="outline"
              className="border-[#F7F5F0]/40 bg-[#F7F5F0]/10 backdrop-blur-sm text-[#F7F5F0] hover:bg-[#F7F5F0]/20 hover:text-[#F7F5F0] rounded-full px-7 sm:px-8 h-12 text-lg font-semibold"
              data-testid="hero-cta-secondary"
            >
              How It Works
            </Button>
          </motion.div>

          <motion.div variants={item} className="mt-8 sm:mt-10 flex flex-wrap gap-x-6 sm:gap-x-8 gap-y-3">
            <Pill icon={<ShieldCheck className="w-4 h-4" />} text="No upfront fees — ever" />
            <Pill icon={<DollarSign className="w-4 h-4" />} text="You only pay if we recover" />
          </motion.div>
        </motion.div>

        {/* Floating stat card bottom-right (desktop only) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="hidden md:block absolute bottom-10 right-6 lg:right-10 bg-[#F7F5F0]/95 backdrop-blur-md rounded-2xl px-6 py-5 border border-[#F7F5F0]/40 shadow-2xl max-w-xs"
        >
          <p className="text-xs text-[#64748B] uppercase tracking-wider">Average recovery</p>
          <p className="font-serif-display font-bold text-4xl text-[#0F172A] mt-1">$12K – $85K<span className="text-[#FF008F]">+</span></p>
          <p className="text-xs text-[#475569] mt-1">per qualified case</p>
        </motion.div>
      </div>
    </section>
  );
}

function Pill({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-xs sm:text-sm text-[#F7F5F0]/90">
      <span className="text-[#FF008F]">{icon}</span>
      {text}
    </div>
  );
}
